import SuperTokens from 'supertokens-auth-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import Session from 'supertokens-auth-react/recipe/session';
import SessionWebsite from 'supertokens-website';

let initialized = false;

interface InitOptions {
  /** Same-origin URL of the API (e.g. `https://app.invoyss.com`). */
  apiDomain: string;
  /** Mount path for SuperTokens routes (matches server `apiBasePath`). */
  apiBasePath?: string;
}

/**
 * Boot the SuperTokens browser SDK once per page load.
 *
 * We use `supertokens-auth-react` for its React-aware session helpers
 * AND `supertokens-website` for its underlying axios interceptors.
 * The SuperTokens session relies on cookies set by the server (the
 * server is configured with `getTokenTransferMethod: () => 'any'`,
 * which falls back to cookies when no `st-auth-mode: header` request
 * header is present — exactly what the web SDK does by default).
 *
 * `cookiesEnabled: true` (default) means the SDK will NOT set the
 * `st-auth-mode: header` request header on outgoing calls — the
 * server then issues HttpOnly session cookies in the response. The
 * mobile SDK explicitly sends `st-auth-mode: header` and gets bearer
 * tokens back, so the same backend serves both.
 */
export function initSuperTokensWeb(options: InitOptions): void {
  if (initialized) return;
  SuperTokens.init({
    appInfo: {
      appName: 'Invoice Studio',
      apiDomain: options.apiDomain,
      websiteDomain: options.apiDomain,
      apiBasePath: options.apiBasePath ?? '/api/auth',
      websiteBasePath: '/auth',
    },
    recipeList: [
      EmailPassword.init(),
      EmailVerification.init({ mode: 'REQUIRED' }),
      Session.init({
        // Refresh tokens automatically on 401 — the SDK retries the
        // failed request once the refresh succeeds. Same behaviour as
        // mobile, just driven by cookies instead of bearer headers.
        sessionExpiredStatusCode: 401,
      }),
    ],
  });
  initialized = true;
}

/**
 * Adapter shape the @repo/api-client SessionProvider expects. Pulls
 * the underlying `supertokens-website` Session module so we can ask
 * `doesSessionExist` without going through the React context (which
 * would force the provider to mount inside a SuperTokensWrapper —
 * we want to keep our own provider tree the source of truth for
 * session state).
 */
export function webSessionAdapter() {
  return {
    async doesSessionExist(): Promise<boolean> {
      try {
        return await SessionWebsite.doesSessionExist();
      } catch {
        return false;
      }
    },
    async signOut(): Promise<void> {
      try {
        await SessionWebsite.signOut();
      } catch {
        // Fail open — caller has already cleared local state.
      }
    },
  };
}
